import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, Loader2 } from "lucide-react";
import { FormEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { OrganizationType } from "~/api/model";
import { useCreateOrganization } from "~/api/organizations/organizations";
import { getGetCurrentUserQueryKey } from "~/api/users/users";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { SKY_ACCOUNT_CREATED } from "~/constants/index";
import {
  isCustomEmailDomain,
  removeAllTokens,
  ROUTES,
  validateEmail,
} from "~/helpers";
import { useTrackAccountCreation } from "~/hooks/useTrackAccountCreation";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { RegisterDemoWidget } from "./RegisterDemoWidget";
import { TermsAndPolicyDialog } from "./TermsAndPolicyDialog";

enum REGISTER_STEP {
  PERSONAL_DETAILS = 1,
  COMPANY_DETAILS = 2,
  PIPELINE_MODE = 3,
  SUCCESS = 4,
  DEMO_REQUEST = 5,
}

type PipelineMode = "USE_TOC" | "USE_SNOWFLAKE" | "USE_SMART_AGENT";

interface FormErrors {
  email?: boolean;
  fullname?: boolean;
  companyName?: boolean;
  companyType?: boolean;
  terms?: boolean;
  duplicateEmail?: boolean;
}

const COMPANY_TYPES = [
  { value: OrganizationType.IME, label: "Insurance" },
  { value: OrganizationType.LOGISTICS, label: "Logistics" },
  { value: OrganizationType.LEGAL, label: "Legal" },
  { value: OrganizationType.FINANCE, label: "Finance" },
  { value: OrganizationType.HOSPITALITY, label: "Hospitality" },
  { value: OrganizationType.ENERGY, label: "Energy" },
  { value: OrganizationType.COMPENSATION, label: "Workers' Compensation" },
  { value: OrganizationType.CONSTRUCTIONS, label: "Construction" },
  { value: OrganizationType.ADVERTISING, label: "Advertising" },
  { value: OrganizationType.MANUFACTURING, label: "Manufacturing" },
  { value: OrganizationType.ZONING, label: "Land Assessment and Zoning" },
  {
    value: OrganizationType.HEALTHCARE,
    label: "Long Term and Rehabilitation Care",
  },
  {
    value: OrganizationType.ALLIED_HEALTH,
    label: "Allied Health Professionals",
  },
  {
    value: OrganizationType.CLINICAL_SPECIALISTS,
    label: "Clinical Specialists",
  },
  {
    value: OrganizationType.LEGAL_HEALTH,
    label: "Legal and Health Professionals",
  },
  {
    value: OrganizationType.UNDERWRITING,
    label: "Underwriting",
  },
];

export const RegisterForm = () => {
  const intl = useIntl();
  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState<OrganizationType | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState<REGISTER_STEP>(
    REGISTER_STEP.PERSONAL_DETAILS
  );
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [pipelineMode, setPipelineMode] =
    useState<PipelineMode>("USE_SMART_AGENT");
  const { mutate, isPending } = useCreateOrganization();
  const { trackCreateAccount } = useTrackAccountCreation();
  const queryClient = useQueryClient();

  const validateFirstStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    } else if (!isCustomEmailDomain(email)) {
      // Temporary: Show inline Calendly for public/free email domains to test lead quality
      setCurrentStep(REGISTER_STEP.DEMO_REQUEST);
      return false;
    }

    if (!fullname.trim()) {
      newErrors.fullname = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSecondStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = true;
      isValid = false;
    }

    if (!companyType) {
      newErrors.companyType = true;
      isValid = false;
    }

    if (!termsAccepted) {
      newErrors.terms = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleContinue = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    removeAllTokens();
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    queryClient.invalidateQueries();

    if (currentStep === REGISTER_STEP.PERSONAL_DETAILS) {
      if (validateFirstStep()) {
        setCurrentStep(REGISTER_STEP.COMPANY_DETAILS);
      }
    } else if (currentStep === REGISTER_STEP.COMPANY_DETAILS) {
      if (validateSecondStep()) {
        setCurrentStep(REGISTER_STEP.PIPELINE_MODE);
      }
    } else if (currentStep === REGISTER_STEP.PIPELINE_MODE) {
      trackEvent({
        eventName: "app/register/register.button/submit",
        properties: {
          email,
          fullname,
          companyName,
          companyType,
          pipelineMode,
        },
      });
      mutate(
        {
          data: {
            ownerEmail: email,
            ownerName: fullname,
            orgName: companyName,
            orgType: companyType ?? OrganizationType.IME,
            pipelineMode,
          } as Parameters<typeof mutate>[0]["data"] & {
            pipelineMode: PipelineMode;
          },
        },
        {
          onSuccess: () => {
            trackCreateAccount();
            localStorage.setItem(SKY_ACCOUNT_CREATED, "true");
            setCurrentStep(REGISTER_STEP.SUCCESS);
          },
          onError: (error: unknown) => {
            const axiosError = error as {
              response?: { status?: number; data?: { code?: number } };
            };
            const httpStatus = axiosError?.response?.status;
            const bodyCode = axiosError?.response?.data?.code;
            const isUserAlreadyExists = httpStatus === 409 || bodyCode === 409;

            if (isUserAlreadyExists) {
              setErrors((prev) => ({ ...prev, duplicateEmail: true }));
              setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS);
            } else {
              logger.error("Registration error:", error);
              setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS);
            }
          },
        }
      );
    }
  };

  return (
    <div className="flex justify-center items-start pt-10">
      <div
        className={`flex flex-col gap-6 w-full ${
          currentStep === REGISTER_STEP.DEMO_REQUEST ? "max-w-4xl" : "max-w-lg"
        }`}
      >
        <h3 className="text-2xl font-semibold text-center text-base-foreground">
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <FormattedMessage
              id="auth.register.title"
              defaultMessage="Create an Account"
            />
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS ? (
            <FormattedMessage
              id="auth.register.companyDetails"
              defaultMessage="Company Details"
            />
          ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
            <FormattedMessage
              id="auth.register.pipelineMode"
              defaultMessage="Document Processing"
            />
          ) : currentStep === REGISTER_STEP.SUCCESS ? (
            <FormattedMessage
              id="auth.register.checkEmail"
              defaultMessage="Check Your Email"
            />
          ) : (
            <FormattedMessage
              id="auth.register.demoRequest"
              defaultMessage="Request a Demo"
            />
          )}
        </h3>
        <form
          onSubmit={handleContinue}
          className="w-full flex flex-col gap-4 text-sm"
        >
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="registerEmail"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.email"
                    defaultMessage="Email"
                  />
                </Label>
                <Input
                  id="registerEmail"
                  placeholder={intl.formatMessage({
                    id: "auth.register.emailPlaceholder",
                    defaultMessage: "Enter your email",
                  })}
                  value={email}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                    errors.email ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
                {errors.email && (
                  <span className="text-red-600">
                    <FormattedMessage
                      id="auth.register.emailError"
                      defaultMessage="Email should be <strong>example@site.com</strong>"
                      values={{
                        strong: (chunks) => (
                          <strong className="font-semibold">{chunks}</strong>
                        ),
                      }}
                    />
                  </span>
                )}
                {errors.duplicateEmail && (
                  <span className="text-red-600">
                    <FormattedMessage
                      id="auth.register.duplicateEmailError"
                      defaultMessage="An account with this email already exists"
                    />
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="registerFullname"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.fullname"
                    defaultMessage="Full name"
                  />
                </Label>
                <Input
                  id="registerFullname"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "auth.register.fullnamePlaceholder",
                    defaultMessage: "Enter your full name",
                  })}
                  value={fullname}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                    errors.fullname ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setFullname(e.target.value);
                  }}
                />
                {errors.fullname && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.fullnameError"
                      defaultMessage="This field cannot be empty"
                    />
                  </span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS ? (
            <>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="companyName"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.companyName"
                    defaultMessage="Company name"
                  />
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "auth.register.companyNamePlaceholder",
                    defaultMessage: "Enter your company name",
                  })}
                  value={companyName}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.companyName ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                  }}
                />
                {errors.companyName && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyNameError"
                      defaultMessage="Company name cannot be empty"
                    />
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="companyType"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.companyType"
                    defaultMessage="Type of company"
                  />
                </Label>
                <Select
                  value={companyType}
                  onValueChange={(value) => {
                    setCompanyType(value as OrganizationType);
                  }}
                >
                  <SelectTrigger
                    className={`w-full ${errors.companyType ? "border-red-600" : "border-gray-300"} ${companyType ? "text-base-foreground" : "text-muted-foreground"}`}
                  >
                    <SelectValue
                      placeholder={intl.formatMessage({
                        id: "auth.register.companyTypePlaceholder",
                        defaultMessage: "Select company type",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <FormattedMessage
                          id={`auth.register.companyType.${type.value}`}
                          defaultMessage={type.label}
                        />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.companyType && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyTypeError"
                      defaultMessage="Please select the type of company"
                    />
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-start space-x-2 py-2">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => {
                      setTermsAccepted(checked as boolean);
                    }}
                    className={`cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border border-[#E4E4E7] shadow-none ${errors.terms ? "border-red-600" : ""}`}
                  />
                  <div className="flex flex-col gap-1">
                    <Label className="font-medium text-base-foreground cursor-default">
                      <FormattedMessage
                        id="auth.register.termsAgreement"
                        defaultMessage="Agree to the {terms} and {privacy}"
                        values={{
                          terms: (
                            <TermsAndPolicyDialog type="terms">
                              <span className="text-blue-600 cursor-pointer">
                                <FormattedMessage
                                  id="auth.register.terms"
                                  defaultMessage="Terms and Conditions"
                                />
                              </span>
                            </TermsAndPolicyDialog>
                          ),
                          privacy: (
                            <TermsAndPolicyDialog type="policy">
                              <span className="text-blue-600 cursor-pointer">
                                <FormattedMessage
                                  id="auth.register.privacy"
                                  defaultMessage="Privacy Policy"
                                />
                              </span>
                            </TermsAndPolicyDialog>
                          ),
                        }}
                      />
                    </Label>
                  </div>
                </div>
                {errors.terms && (
                  <span className="text-red-600">
                    <FormattedMessage
                      id="auth.register.termsError"
                      defaultMessage="You must agree to the Terms and Conditions and Privacy Policy"
                    />
                  </span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
            <div className="flex flex-col gap-4">
              <p className="text-center text-muted-foreground mb-2">
                <FormattedMessage
                  id="auth.register.pipelineModeDescription"
                  defaultMessage="Choose how Sky AI should process your documents"
                />
              </p>
              <div className="space-y-3">
                <div
                  onClick={() => setPipelineMode("USE_TOC")}
                  className={`
                    relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-all
                    ${pipelineMode === "USE_TOC" ? "border-sky-600 ring-2 ring-sky-600 bg-sky-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white"}
                  `}
                >
                  <div className="flex flex-1 flex-col">
                    <span className="block text-sm font-medium text-slate-900">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useToc"
                        defaultMessage="Table of Contents"
                      />
                    </span>
                    <span className="mt-1 text-sm text-slate-500">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useTocDescription"
                        defaultMessage="Uses the document's table of contents to identify sections. Best for well-structured documents with clear TOC."
                      />
                    </span>
                  </div>
                </div>
                <div
                  onClick={() => setPipelineMode("USE_SNOWFLAKE")}
                  className={`
                    relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-all
                    ${pipelineMode === "USE_SNOWFLAKE" ? "border-sky-600 ring-2 ring-sky-600 bg-sky-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white"}
                  `}
                >
                  <div className="flex flex-1 flex-col">
                    <span className="block text-sm font-medium text-slate-900">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useSnowflake"
                        defaultMessage="AI Processing Only"
                      />
                    </span>
                    <span className="mt-1 text-sm text-slate-500">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useSnowflakeDescription"
                        defaultMessage="AI analyzes content to identify logical sections. Best for documents without clear structure."
                      />
                    </span>
                  </div>
                </div>
                <div
                  onClick={() => setPipelineMode("USE_SMART_AGENT")}
                  className={`
                    relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none transition-all
                    ${pipelineMode === "USE_SMART_AGENT" ? "border-sky-600 ring-2 ring-sky-600 bg-sky-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white"}
                  `}
                >
                  <span className="absolute -top-2 right-3 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                    <FormattedMessage
                      id="auth.register.pipelineMode.recommended"
                      defaultMessage="Recommended"
                    />
                  </span>
                  <div className="flex flex-1 flex-col">
                    <span className="block text-sm font-medium text-slate-900">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useSmartAgent"
                        defaultMessage="Smart AI Agent"
                      />
                    </span>
                    <span className="mt-1 text-sm text-slate-500">
                      <FormattedMessage
                        id="auth.register.pipelineMode.useSmartAgentDescription"
                        defaultMessage="Automatically chooses the best method based on document analysis. Recommended for most use cases."
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : currentStep === REGISTER_STEP.SUCCESS ? (
            <div className="flex flex-col gap-4">
              <p className="text-center text-muted-foreground">
                <FormattedMessage
                  id="auth.register.verificationEmail"
                  defaultMessage="We've emailed you a temporary password and registration link. If not received within an hour, check spam or contact support."
                  values={{
                    support: (chunks) => (
                      <a
                        href="mailto:support@usesky.ai"
                        className="text-blue-600 hover:underline"
                      >
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </p>
              <Button asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.backToLogin"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="w-full max-w-[1400px] mx-auto px-4 flex justify-center">
              <RegisterDemoWidget
                email={email}
                name={fullname.trim()}
                onBack={() => setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS)}
              />
            </div>
          )}
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <>
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
              <span className="text-sm text-muted-foreground font-medium text-center">
                <FormattedMessage id="auth.register.or" defaultMessage="or" />
              </span>
              <Button variant="secondary" asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.login"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS ? (
            <>
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS)}
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                <FormattedMessage
                  id="auth.register.back"
                  defaultMessage="Back"
                />
              </Button>
            </>
          ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
            <>
              <Button disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <FormattedMessage
                  id={
                    isPending
                      ? "auth.register.creatingAccount"
                      : "auth.register.createAccount"
                  }
                  defaultMessage={
                    isPending ? "Creating account..." : "Create Account"
                  }
                />
              </Button>
              <Button
                type="button"
                disabled={isPending}
                variant="secondary"
                onClick={() => setCurrentStep(REGISTER_STEP.COMPANY_DETAILS)}
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                <FormattedMessage
                  id="auth.register.back"
                  defaultMessage="Back"
                />
              </Button>
            </>
          ) : null}
        </form>
      </div>
    </div>
  );
};
